import { App as AntdApp } from 'antd';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';

interface UseFormNavigationGuardOptions {
  isDirty: boolean;
  message?: string;
  enabled?: boolean;
  /**
   * A transition to let through even while dirty, because it moves between two URLs of the
   * SAME in-progress form rather than away from it — e.g. a multi-step screen's own step 0 and
   * its composer. Absent, no transition is exempt, which is every existing caller's behaviour.
   */
  isExemptTransition?: (currentPathname: string, nextPathname: string) => boolean;
}

// Global state object to allow bypassing navigation guard (used after successful form submission)
export const navigationBypassState = {
  allow: false,
};

/**
 * The browser's own "Reload site? / Leave site?" prompt asks exactly the question our modal asks,
 * and the answer is final either way: the document unloads, or the user chose to stay. Asking
 * again in-app re-asks a question that has already been answered.
 *
 * There is no event for "the native prompt was dismissed", so this is inferred instead of timed:
 * `beforeunload` fired and we are still running ⇒ the user answered "stay". The flag is cleared
 * on the user's next real interaction with the page, because a navigation they start *after* that
 * is a fresh question and does deserve the modal. Clearing on interaction rather than on a timer
 * matters — the native prompt has no time bound, and the Cancel click lands on browser chrome, so
 * it produces no event of its own.
 */
const nativePromptState = {
  answeredStay: false,
};

/**
 * hook to prevent navigation when form has unsaved changes
 *
 */
export function useFormNavigationGuard({
  isDirty,
  message,
  enabled = true,
  isExemptTransition,
}: UseFormNavigationGuardOptions) {
  const { modal } = AntdApp.useApp();
  const { t } = useTranslation();
  const confirmedRef = useRef(false);
  const modalRef = useRef<{ destroy: () => void } | null>(null);

  // Block navigation when form is dirty
  const blocker = useBlocker(
    useCallback(
      ({
        currentLocation,
        nextLocation,
      }: {
        currentLocation: { pathname: string };
        nextLocation: { pathname: string };
      }) => {
        // Don't block if bypass flag is set (after successful submission)
        if (navigationBypassState.allow) {
          navigationBypassState.allow = false; // Reset flag for next time
          return false;
        }

        // Don't block if not enabled or not dirty
        if (!enabled || !isDirty) return false;

        // Don't block if already confirmed
        if (confirmedRef.current) return false;

        // Block if navigating to a different location — unless the caller has said the two
        // locations are the same form.
        if (currentLocation.pathname === nextLocation.pathname) return false;
        return !isExemptTransition?.(currentLocation.pathname, nextLocation.pathname);
      },
      [enabled, isDirty, isExemptTransition]
    )
  );

  useEffect(() => {
    if (!modal) return;

    if (blocker.state !== 'blocked') {
      modalRef.current?.destroy();
      modalRef.current = null;
      return;
    }

    if (modalRef.current) return;

    // The browser already asked, and the user already said "stay" — don't ask a second time.
    if (nativePromptState.answeredStay) {
      nativePromptState.answeredStay = false;
      blocker.reset();
      return;
    }

    modalRef.current = modal.confirm({
      title: t('form-navigation-guard-title'),
      content: message ?? t('form-navigation-guard-message'),
      okText: t('form-navigation-guard-leave'),
      cancelText: t('form-navigation-guard-stay'),
      okType: 'danger',
      cancelButtonProps: { type: 'primary' },
      autoFocusButton: 'cancel',
      onOk: () => {
        confirmedRef.current = true;
        blocker.proceed();
        modalRef.current?.destroy();
        modalRef.current = null;
      },
      onCancel: () => {
        blocker.reset();
        modalRef.current?.destroy();
        modalRef.current = null;
      },
    });

    return () => {
      modalRef.current?.destroy();
      modalRef.current = null;
    };
  }, [blocker, message, modal, t]);

  // Handle browser back/forward/refresh
  useEffect(() => {
    if (!enabled || !isDirty) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers ignore custom messages, but setting returnValue is still required
      e.returnValue = '';

      // If we are still alive after this, the user answered the native prompt with "stay".
      nativePromptState.answeredStay = true;
    };

    // Any real interaction with the page means the user has moved on; the next block they cause
    // is a new question, not the one the browser already asked.
    const clearNativePromptAnswer = () => {
      nativePromptState.answeredStay = false;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pointerdown', clearNativePromptAnswer, true);
    window.addEventListener('keydown', clearNativePromptAnswer, true);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pointerdown', clearNativePromptAnswer, true);
      window.removeEventListener('keydown', clearNativePromptAnswer, true);
    };
  }, [enabled, isDirty]);

  // Reset confirmed flag when dirty state changes
  useEffect(() => {
    if (!isDirty) {
      confirmedRef.current = false;
    }
  }, [isDirty]);

  return {
    isBlocked: blocker.state === 'blocked',
    isDirty,
  };
}
